import {fetchClient} from './client'

/**
 * GET /wms/task-lists/{id}/print/?output=pdf|xlsx (см. wms-print-api.md) — не описан в
 * codegen-схеме (тот же случай, что и /wms/media/* в media.ts): бинарный файл, а не JSON,
 * поэтому идём в обход типизации openapi-fetch тем же приёмом (эрайзинг типа + parseAs: 'blob').
 * Успешный ответ одновременно фиксирует печать на бэке (printed_at/print_count/audit) — сама
 * загрузка файла и есть событие «напечатано», второго вызова подтверждения нет.
 */
type RawGet = (
  path: string,
  options?: {parseAs?: 'blob'}
) => Promise<{data?: unknown; error?: unknown; response?: Response}>
type RawPost = (
  path: string,
  options?: {body?: unknown; parseAs?: 'blob'}
) => Promise<{data?: unknown; error?: unknown; response?: Response}>

const filenameFromDisposition = (h: string | null, fallback: string): string => {
  if (!h) return fallback
  const m = /filename="?([^";]+)"?/i.exec(h)
  return m ? m[1] : fallback
}

/**
 * Открывает PDF в скрытом iframe и вызывает системный диалог печати, как только он
 * загрузился — тот же приём, что и у большинства «печать по клику» кнопок, раз
 * Content-Disposition: attachment не даёт открыть файл прямо во вкладке. Освобождает
 * object-URL сам, поэтому вызывающий код в этом случае URL.revokeObjectURL не зовёт.
 */
const printBlobPdf = (url: string): void => {
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.src = url

  const cleanup = () => {
    iframe.remove()
    URL.revokeObjectURL(url)
  }

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } catch {
      // молча пропускаем — файл уже скачан, печать «по возможности»
    }
    // afterprint не всегда всплывает надёжно для PDF-вьюера внутри iframe —
    // подчищаем по таймауту как страховку, если диалог печати уже закрыт раньше.
    setTimeout(cleanup, 60_000)
  }

  document.body.appendChild(iframe)
}

/**
 * PDF: скачивание за клиента не делаем вовсе — печать фиксируется на бэке уже самим
 * GET-запросом (см. комментарий выше), а `<a download>` для application/pdf blob-урлов
 * в некоторых браузерах игнорируется и открывает вкладку вместо скачивания. Вместо
 * этого сразу зовём системный диалог печати через скрытый iframe.
 * Excel скачиваем как обычный файл — открыть его «на печать» смысла нет.
 */
export const printTaskList = async (
  listId: number,
  sheetNumber: string,
  output: 'pdf' | 'xlsx' = 'pdf'
): Promise<void> => {
  const {data, error, response} = await (fetchClient.GET as unknown as RawGet)(
    `/wms/task-lists/${listId}/print/?output=${output}`,
    {parseAs: 'blob'}
  )
  const status = (response as Response | undefined)?.status
  if (error || !data) {
    if (status === 400) throw new Error('Список отменён — печатать нечего.')
    if (status === 404) throw new Error('Список не найден.')
    throw new Error('Не удалось распечатать список.')
  }
  const url = URL.createObjectURL(data as Blob)

  if (output === 'pdf') {
    printBlobPdf(url)
    return
  }

  const a = document.createElement('a')
  a.href = url
  a.download = `${sheetNumber}.${output}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * POST /wms/cells/print/ (см. wms-storage-api.md) — «Печать листа по отмеченным ячейкам».
 * Один запрос одновременно создаёт новый нумерованный CellSheet (CS-<id>) и возвращает
 * бинарник — нет отдельного шага «создать → получить id → распечатать GET'ом», в отличие
 * от сборочного листа. Пустой cellIds — 400 на бэке; вызывающий код не должен звать это
 * с пустым списком (кнопка печати скрыта/задизейблена, пока ничего не выбрано).
 */
export const printCellSheet = async (
  cellIds: number[],
  output: 'pdf' | 'xlsx' = 'pdf'
): Promise<void> => {
  const {data, error, response} = await (fetchClient.POST as unknown as RawPost)(
    '/wms/cells/print/',
    {body: {cells: cellIds, output}, parseAs: 'blob'}
  )
  const status = (response as Response | undefined)?.status
  if (error || !data) {
    if (status === 400) throw new Error('Выберите хотя бы одну ячейку.')
    throw new Error('Не удалось распечатать лист ячеек.')
  }
  const url = URL.createObjectURL(data as Blob)
  const filename = filenameFromDisposition(
    response?.headers.get('content-disposition') ?? null,
    `cell-sheet.${output}`
  )

  if (output === 'pdf') {
    printBlobPdf(url)
    return
  }

  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * POST /wms/cells/labels/print/ (см. wms-cell-bulk-labels.md, SCRUM-503) — лист
 * этикеток (код сверху, штрих код Code128 снизу, 15 на A4) для любой подборки
 * ячеек — как только что созданных генератором, так и любой ручной подборки для
 * перепечатки сорванной этикетки. PDF-only (нет output-параметра, в отличие от
 * printCellSheet), сама печать фиксируется тем же приёмом — iframe + системный
 * диалог печати, а не файл на диск (см. Use Case тикета: «открывается на печать
 * автоматически»).
 */
export const printCellLabels = async (cellIds: number[]): Promise<void> => {
  const {data, error, response} = await (fetchClient.POST as unknown as RawPost)(
    '/wms/cells/labels/print/',
    {body: {cells: cellIds}, parseAs: 'blob'}
  )
  const status = (response as Response | undefined)?.status
  if (error || !data) {
    if (status === 400) throw new Error('Выберите хотя бы одну ячейку.')
    throw new Error('Не удалось напечатать этикетки.')
  }
  const url = URL.createObjectURL(data as Blob)
  printBlobPdf(url)
}

/**
 * GET /wms/employees/{id}/badge/ (см. wms-employees-api.md, SCRUM-509) — CR80-карта
 * (54×86 мм) сотрудника: лого, фото/силуэт, ФИО, плашка роли, крупный Code128
 * штрих код бейджа без подписи под ним. Только view-скоуп, не manage — печатать
 * чужой бейдж может и OPERATOR.
 */
export const printEmployeeBadge = async (employeeId: number): Promise<void> => {
  const {data, error, response} = await (fetchClient.GET as unknown as RawGet)(
    `/wms/employees/${employeeId}/badge/`,
    {parseAs: 'blob'}
  )
  const status = (response as Response | undefined)?.status
  if (error || !data) {
    if (status === 400) throw new Error('Не удалось напечатать бейдж.')
    if (status === 404) throw new Error('Сотрудник не найден.')
    throw new Error('Не удалось напечатать бейдж.')
  }
  const url = URL.createObjectURL(data as Blob)
  printBlobPdf(url)
}
